import { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';

import ConcurrentReviewComparisonTab from 'components/patient/concurrentReview/ConcurrentReviewComparisonTab';
import RenderingErrorBoundary from 'components/RenderingErrorBoundary';
import FilesTab from 'components/shared/ai/summary/FilesTab';
import SummaryTab from 'components/shared/ai/summary/SummaryTab';
import Button from 'components/shared/Button';
import Foldout from 'components/shared/Foldout';
import Tabs, { TabType } from 'components/Tabs';
import { Flags } from 'constants/flags';
import { useProfile } from 'context/profile';
import { useAuthorization } from 'context/review/authorization';
import { simpleDate } from 'lib/date';
import Authorization from 'models/reviews/Authorization';
import { colors } from 'styles/theme/colors';
import { H3, Label } from 'styles/typography';

export type TabTypeValue = 'files' | 'summary' | 'comparison';

type ConcurrentReviewSlideoutProps = {
  open?: boolean;
  onClose?: (authorization?: Authorization) => void;
  onUpdateAuthorization?: () => void;
  initialTabValue?: TabTypeValue;
  onTabChange?: (tabValue: TabTypeValue) => void;
};

export default function ConcurrentReviewSlideout({
  open = true,
  onClose,
  onUpdateAuthorization,
  initialTabValue,
  onTabChange,
}: ConcurrentReviewSlideoutProps) {
  const { profile } = useProfile();
  const { authorization, setAllowRefetch } = useAuthorization();

  useEffect(() => {
    setAllowRefetch(open);
  }, [open, setAllowRefetch]);

  const approvedThroughDate = useMemo(() => {
    return authorization?.approvedThroughDate ? simpleDate(authorization.approvedThroughDate) : undefined;
  }, [authorization?.approvedThroughDate]);

  const lastReviewApprovedThroughDate = useMemo(() => {
    return authorization?.previousAuthorizationReview?.approvedThroughDate
      ? simpleDate(authorization.previousAuthorizationReview.approvedThroughDate)
      : undefined;
  }, [authorization?.previousAuthorizationReview?.approvedThroughDate]);

  const { summaryJob, summary, comparisonJob, comparison } = authorization ?? {};
  const isSummarized = !!summary;
  const isCompared = !!comparison;

  const tabs: TabType[] = useMemo(
    () => [
      {
        label:
          authorization?.attachments?.length === undefined ? 'Files' : `Files (${authorization?.attachments?.length})`,
        value: 'files',
      },
      {
        label: 'Summary',
        value: 'summary',
      },
      ...(profile.hasFlag(Flags.ComparisonForAiSummary) && lastReviewApprovedThroughDate
        ? [
            {
              label: `Comparison to ${lastReviewApprovedThroughDate}`,
              value: 'comparison',
            },
          ]
        : []),
    ],
    [profile, authorization?.attachments?.length, lastReviewApprovedThroughDate]
  );

  const [selectedTab, setSelectedTab] = useState<TabType>(() => {
    const initialTab = initialTabValue && tabs.find((tab) => tab.value === initialTabValue);

    return initialTab || tabs[0];
  });

  useEffect(() => {
    if (initialTabValue && open) {
      const initialTab = tabs.find((tab) => tab.value === initialTabValue);
      if (initialTab && selectedTab.value !== initialTabValue) {
        setSelectedTab(initialTab);
      }
    }
    // we deliberately don't want to re-run this effect when the selectedTab changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialTabValue, open]);

  const handleTabClick = (tab: TabType) => {
    setSelectedTab(tab);
    onTabChange?.(tab.value as TabTypeValue);
  };

  const pendingActions = useRef<Array<'summarize' | 'compare'>>([]);

  const handleSwitchToTab = (tabValue: TabTypeValue) => {
    const tab = tabs.find((tab) => tab.value === tabValue);
    if (tab) {
      setSelectedTab(tab);
      onTabChange?.(tabValue);
    }
  };

  const handleSummarizeFromFilesTab = () => {
    pendingActions.current.push('summarize');
    handleSwitchToTab('summary');
  };

  const handleCompareFromSummaryTab = () => {
    pendingActions.current.push('compare');
    handleSwitchToTab('comparison');
  };

  const closeModal = () => {
    onClose?.(authorization);
  };

  const handleUpdateAuthorization = () => {
    closeModal();
    onUpdateAuthorization?.();
  };

  return (
    <Foldout open={open} width='min(100%, 1200px)' onBackdropClick={closeModal}>
      <Container>
        <FilterFoldoutCloseIcon onClick={closeModal}>&times;</FilterFoldoutCloseIcon>
        <FilterFoldoutHeader>
          <Header>
            <H3>Approval Through {approvedThroughDate ?? 'N/A'}</H3>
            {onUpdateAuthorization && (
              <AlignFloater>
                <UpdateAuthorizationButton variant='outline-primary' onClick={handleUpdateAuthorization}>
                  Update Authorization
                </UpdateAuthorizationButton>
              </AlignFloater>
            )}
          </Header>
          <Label>#{authorization?.data.authorizationNumber}</Label>
        </FilterFoldoutHeader>
        <TabsContainer>
          <Tabs tabs={tabs} onTabClick={handleTabClick} selectedTab={selectedTab} />
        </TabsContainer>
        <Content>
          {authorization && (
            <>
              {selectedTab.value === 'files' && (
                <FilesTab
                  locationEpisodeId={authorization.locationEpisodeId || ''}
                  review={authorization}
                  isSummarized={isSummarized}
                  files={authorization.attachments ?? []}
                  onSummarizeFiles={handleSummarizeFromFilesTab}
                />
              )}
              {selectedTab.value === 'summary' && (
                <RenderingErrorBoundary key={summaryJob?.id}>
                  <SummaryTab
                    review={authorization}
                    pendingActions={pendingActions.current}
                    isCompared={isCompared}
                    onCompareSummaries={handleCompareFromSummaryTab}
                  />
                </RenderingErrorBoundary>
              )}
              {selectedTab.value === 'comparison' && (
                <RenderingErrorBoundary key={comparisonJob?.id}>
                  <ConcurrentReviewComparisonTab
                    authorization={authorization}
                    pendingActions={pendingActions.current}
                    isSummarized={isSummarized && !!authorization.previousAuthorizationReview?.summary}
                  />
                </RenderingErrorBoundary>
              )}
            </>
          )}
        </Content>
      </Container>
    </Foldout>
  );
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const Header = styled.div`
  display: flex;
  gap: 16px;
  align-items: center;
`;

const FilterFoldoutCloseIcon = styled.div`
  color: ${colors.black75};
  cursor: pointer;
  font-size: 24px;
  position: absolute;
  right: 24px;
  top: 16px;
`;

const FilterFoldoutHeader = styled.div`
  display: flex;
  flex-direction: column;

  padding: 40px 40px 0;
`;

const TabsContainer = styled.div`
  border-bottom: 1px solid ${colors.black10};
  padding: 24px 40px 0;
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 40px;
  flex: 1;
  padding: 24px 40px 40px;
  overflow-y: auto;
`;

const AlignFloater = styled.div`
  height: 0;
  display: flex;
  align-items: center;
`;

const UpdateAuthorizationButton = styled(Button)`
  padding: 8px 16px;
  font-size: 12px;
`;
